declare global {
  interface Window {
    readonly dataLayer?: Array<IArguments | Record<string, unknown>>
  }
}

const fromData = (prop: string, data: unknown): string | undefined => {
  return typeof data === "object" &&
    data !== null &&
    prop in data &&
    typeof data[prop as keyof typeof data] === "string"
    ? data[prop as keyof typeof data]
    : undefined
}

export const loadGoogleTagManager = (gtmAppId: string) => {
  gtagCommand(
    "policy",
    "all",
    function (_containerId: string, permissionId: string, data: unknown) {
      switch (permissionId) {
        case "inject_script":
          const permittedScripts: RegExp[] = [
            /^https:\/\/cdn-cookieyes\.com\/client_data\/[^\/]+\/script\.js$/,

            // Microsoft Ads
            /^https:\/\/bat\.bing\.com\/.*$/,
          ]

          const url = fromData("url", data) ?? ""

          return permittedScripts.some(regex => regex.test(url))

        case "access_globals":
          const permittedKeys = new Set([
            "gtag",
            "dataLayer",

            // Microsoft
            "uetq",
            "clarity",

            // TikTok
            "ttq",
            "ttq.identify",
            "ttq.track",
          ])

          const key = fromData("key", data) ?? ""
          return permittedKeys.has(key)

        case "access_local_storage":
          return false

        default:
          return true
      }
    }
  )

  gtagCommand("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  })

  gtagObject({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  })

  const gtmScriptTag = document.createElement("script")
  gtmScriptTag.defer = true
  gtmScriptTag.src = `https://www.googletagmanager.com/gtm.js?id=${gtmAppId}`
  document.head.appendChild(gtmScriptTag)
}

/* A Bit Weird: The gtag function
 *
 * Google Tag Manager expects the _elements_ of dataLayer Array to be either
 * `Object`s or `Argument`s but NOT `Array`s.
 *
 * The easiest thing to do is to use the javascript `arguments` object which
 * has type `Arguments`. To do that we must NOT use an arrow function.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/arguments
 *
 * The type signature is added to make typescript happy. Otherwise it complains
 * about the function having 0 parameters when we call it.
 *
 * The `(...args: unknown[])` bit is intentionally only part of the type
 * signature, leaving the _definition_ of the function with an empty paramater
 * list. This isn't increadibly important, but it avoids the rest syntax being
 * transpiled to ES5 and then being completely unused.
 *
 * Note that this only works for command such as `gtagCommand("consent", "default", {...})`.
 * To push regular objects, such as registering an event, use 'gtagObject'.
 */
export const gtagCommand: (...args: unknown[]) => void = function () {
  //@ts-expect-error
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(arguments)
}

/* Should be used to push objects to the 'gtag' otherwise
 * GTM interprets the object as `Argument` which it expects
 * to be a command.
 */
export const gtagObject = (arg: Record<string, unknown>): void => {
  //@ts-expect-error
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(arg)
}
